import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Trophy,
  LineChart,
  Clock,
  LayoutGrid,
  Gauge,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { analyticsApi, reportingApi } from '../../lib/api';

import {
  PerformanceSummary,
  ReportingStatsResponse,
  TimeOfDayBucket,
  PatternMetrics,
  SectorMetrics,
  ExecutionQuality,
} from '../../types/titan';

interface PerformanceAnalyticsProps {
  assetClass?: string;
}

// ─── Pure helpers (defined outside component to avoid recreation) ─────────────

const getProfitFactorColor = (pf: number) => {
  if (pf >= 1.5) return 'text-emerald-500';
  if (pf >= 1.0) return 'text-amber-500';
  return 'text-rose-500';
};

const getPnlColor = (pnl: number) => {
  if (pnl > 0) return 'text-emerald-500';
  if (pnl < 0) return 'text-rose-500';
  return 'text-muted-foreground';
};

const formatDollars = (v: number) => {
  const sign = v >= 0 ? '+' : '';
  return `${sign}$${v.toFixed(2)}`;
};

const formatMinutes = (m: number) => {
  if (m < 60) return `${m.toFixed(0)}m`;
  const h = Math.floor(m / 60);
  const mins = Math.round(m % 60);
  return `${h}h ${mins}m`;
};

// ─── Sub-components defined OUTSIDE parent to prevent recreation on render ────

interface PeriodSelectProps {
  value: number;
  onChange: (v: number) => void;
}

const PeriodSelect: React.FC<PeriodSelectProps> = ({ value, onChange }) => (
  <Select value={String(value)} onValueChange={(v) => onChange(Number(v))}>
    <SelectTrigger className="w-[120px]">
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="7">7 days</SelectItem>
      <SelectItem value="14">14 days</SelectItem>
      <SelectItem value="30">30 days</SelectItem>
      <SelectItem value="60">60 days</SelectItem>
      <SelectItem value="90">90 days</SelectItem>
      <SelectItem value="180">180 days</SelectItem>
      <SelectItem value="365">365 days (YTD window)</SelectItem>
    </SelectContent>
  </Select>
);

interface PeriodRollupCardProps {
  label: string;
  metrics: { total_trades: number; wins: number; losses: number; breakeven?: number; total_pnl: number };
}

const PeriodRollupCard: React.FC<PeriodRollupCardProps> = ({ label, metrics }) => (
  <Card>
    <CardContent className="p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-lg font-bold ${getPnlColor(metrics.total_pnl)}`}>
        {formatDollars(metrics.total_pnl)}
      </div>
      <div className="text-xs text-muted-foreground block mt-1">
        {metrics.total_trades} trades · {metrics.wins}W / {metrics.losses}L
        {(metrics.breakeven ?? 0) > 0 ? ` / ${metrics.breakeven}BE` : ''}
      </div>
      <div className="text-[10px] text-muted-foreground">Realized · ET</div>
    </CardContent>
  </Card>
);

// ─── Main component ───────────────────────────────────────────────────────────

const PerformanceAnalytics: React.FC<PerformanceAnalyticsProps> = ({ assetClass }) => {
  const [periodDays, setPeriodDays] = useState(30);
  const [periodStats, setPeriodStats] = useState<ReportingStatsResponse | null>(null);
  const [summary, setSummary] = useState<PerformanceSummary | null>(null);
  const [timeOfDay, setTimeOfDay] = useState<TimeOfDayBucket[]>([]);
  const [patterns, setPatterns] = useState<PatternMetrics[]>([]);
  const [sectors, setSectors] = useState<SectorMetrics[]>([]);
  const [execQuality, setExecQuality] = useState<ExecutionQuality | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const statsPromise = assetClass
        ? Promise.resolve(null)
        : reportingApi.stats().catch(() => null);

      const [statsRes, summaryRes, todRes, patternRes, sectorRes, execQualityRes] =
        await Promise.allSettled([
          statsPromise,
          reportingApi.summary(periodDays, assetClass),
          reportingApi.byTimeOfDay(periodDays, assetClass),
          reportingApi.byPattern(periodDays, assetClass),
          assetClass ? Promise.resolve({ sectors: [] }) : analyticsApi.bySector(periodDays),
          reportingApi.executionQuality(periodDays, assetClass),
        ]);

      if (statsRes.status === 'fulfilled' && statsRes.value) {
        setPeriodStats(statsRes.value);
      } else {
        setPeriodStats(null);
      }
      if (summaryRes.status === 'fulfilled') setSummary(summaryRes.value);
      if (todRes.status === 'fulfilled') setTimeOfDay(todRes.value.buckets || []);
      if (patternRes.status === 'fulfilled') {
        const rows = patternRes.value.patterns || [];
        setPatterns(
          rows.map((p) => ({
            ...p,
            trades: p.trades ?? (p as PatternMetrics & { total_trades?: number }).total_trades ?? 0,
          })),
        );
      }
      if (sectorRes.status === 'fulfilled') setSectors(sectorRes.value.sectors || []);
      if (execQualityRes.status === 'fulfilled') setExecQuality(execQualityRes.value);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to fetch analytics';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [periodDays, assetClass]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Memoize the stat rows array to avoid recreation on every render
  const statRows = useMemo(() => [
    { label: 'Total P&L',    value: formatDollars(summary?.total_pnl ?? 0),    colorClass: getPnlColor(summary?.total_pnl ?? 0) },
    { label: 'Total Trades', value: String(summary?.total_trades ?? 0),          colorClass: 'text-foreground' },
    { label: 'Avg Hold',     value: formatMinutes(summary?.avg_hold_minutes ?? 0), colorClass: 'text-foreground' },
    { label: 'Best Trade',   value: formatDollars(summary?.best_trade ?? 0),     colorClass: 'text-emerald-500' },
    { label: 'Worst Trade',  value: formatDollars(summary?.worst_trade ?? 0),    colorClass: 'text-rose-500' },
    { label: 'Streaks',      value: `${summary?.max_consecutive_wins ?? 0}W / ${summary?.max_consecutive_losses ?? 0}L`, colorClass: 'text-foreground' },
  ], [summary]);

  // Unscoped "all" view: empty history + no broker snapshot — minimal empty state.
  // Vertical-scoped pages omit ``account`` on purpose; still show charts/stats (may be zeros).
  if (!loading && summary && summary.total_trades === 0 && !summary.account && !assetClass) {
    return (
      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Performance Analytics</h2>
          <PeriodSelect value={periodDays} onChange={setPeriodDays} />
        </div>
        <Alert>
          <AlertDescription>
            No closed trades found in the last {periodDays} days. Performance analytics will appear here
            once trades are completed.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Performance Analytics</h2>
        <div className="flex gap-2 items-center">
          <PeriodSelect value={periodDays} onChange={setPeriodDays} />
          <Button variant="ghost" size="icon" onClick={fetchAnalytics} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {periodStats && !assetClass && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            Realized P&amp;L from closed trades (Firestore exit logs). Market day = US/Eastern.
            Broker equity P&amp;L below is whole-account and may differ from Titan realized totals.
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <PeriodRollupCard label="Today" metrics={periodStats.today} />
            <PeriodRollupCard label="7 days" metrics={periodStats.week} />
            <PeriodRollupCard label="30 days (MTD window)" metrics={periodStats.month} />
            <PeriodRollupCard label="YTD" metrics={periodStats.ytd} />
          </div>
        </div>
      )}

      {/* Live metrics: broker account is whole-portfolio; omitted when ``assetClass`` scopes the page. */}
      {(summary?.account || summary?.open_positions != null || summary?.today != null) && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          {summary?.account && (
            <>
              <Card><CardContent className="p-3">
                <div className="text-xs text-muted-foreground">Account Equity</div>
                <div className="text-lg font-bold">${summary.account.equity.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
              </CardContent></Card>
              <Card><CardContent className="p-3">
                <div className="text-xs text-muted-foreground">Today P&L (Broker)</div>
                <div className={`text-lg font-bold ${summary.account.today_pnl >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                  {summary.account.today_pnl >= 0 ? '+' : ''}${summary.account.today_pnl.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </div>
                <div className={`text-xs ${summary.account.today_pnl_pct >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {summary.account.today_pnl_pct >= 0 ? '+' : ''}{summary.account.today_pnl_pct.toFixed(2)}%
                </div>
              </CardContent></Card>
              <Card><CardContent className="p-3">
                <div className="text-xs text-muted-foreground">Buying Power</div>
                <div className="text-lg font-bold">${(summary.account.buying_power || 0).toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</div>
              </CardContent></Card>
            </>
          )}
          {assetClass && !summary?.account && (
            <Card className="col-span-2 sm:col-span-4 lg:col-span-6">
              <CardContent className="p-3">
                <div className="text-xs text-muted-foreground leading-relaxed">
                  Broker equity and buying power are <span className="text-foreground font-medium">not split by vertical</span>.
                  Open positions and today&apos;s closed trades on this row are Titan records for{' '}
                  <span className="font-mono text-foreground">{assetClass}</span> only.
                </div>
              </CardContent>
            </Card>
          )}
          <Card><CardContent className="p-3">
            <div className="text-xs text-muted-foreground">Today Trades</div>
            <div className="text-lg font-bold">{summary.today?.trades || 0}</div>
            <div className="text-xs text-muted-foreground">
              {summary.today?.wins || 0}W / {summary.today?.losses || 0}L
              {(summary.today?.breakeven ?? 0) > 0 ? ` / ${summary.today?.breakeven}BE` : ''}
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-3">
            <div className="text-xs text-muted-foreground">Today Realized</div>
            <div className={`text-lg font-bold ${(summary.today?.pnl || 0) >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
              {(summary.today?.pnl || 0) >= 0 ? '+' : ''}${(summary.today?.pnl || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-3">
            <div className="text-xs text-muted-foreground">Open Positions (Titan)</div>
            <div className="text-lg font-bold">{summary.open_positions?.count || 0}</div>
            {summary.broker_open_positions != null && (
              <div className="text-xs text-muted-foreground">
                Broker: {summary.broker_open_positions}
              </div>
            )}
            <div className={`text-xs ${(summary.open_positions?.unrealized_pnl || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
              Unrealized: {(summary.open_positions?.unrealized_pnl || 0) >= 0 ? '+' : ''}${(summary.open_positions?.unrealized_pnl || 0).toFixed(2)}
            </div>
          </CardContent></Card>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            {loading ? <Skeleton className="h-20" /> : (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <Trophy className="w-4 h-4 text-blue-500" />
                  <span className="text-sm text-muted-foreground">Win Rate</span>
                </div>
                <p className={`text-3xl font-bold ${(summary?.win_rate ?? 0) >= 50 ? 'text-emerald-500' : 'text-rose-500'}`}>
                  {summary?.win_rate.toFixed(1)}%
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {summary?.wins}W / {summary?.losses}L / {summary?.breakeven}BE
                </p>
              </>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            {loading ? <Skeleton className="h-20" /> : (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <LineChart className="w-4 h-4 text-blue-500" />
                  <span className="text-sm text-muted-foreground">Profit Factor</span>
                </div>
                <p className={`text-3xl font-bold ${getProfitFactorColor(summary?.profit_factor ?? 0)}`}>
                  {summary?.profit_factor.toFixed(2)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {(summary?.profit_factor ?? 0) >= 1.5 ? 'Excellent' :
                   (summary?.profit_factor ?? 0) >= 1.0 ? 'Profitable' : 'Needs improvement'}
                </p>
              </>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            {loading ? <Skeleton className="h-20" /> : (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-blue-500" />
                  <span className="text-sm text-muted-foreground">Expectancy</span>
                </div>
                <p className={`text-3xl font-bold ${getPnlColor(summary?.expectancy ?? 0)}`}>
                  {formatDollars(summary?.expectancy ?? 0)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">Expected value per trade</p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Stats Row — stable keys using label */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
        {statRows.map((stat) => (
          <div key={stat.label} className="rounded-lg border p-3 text-center">
            {loading ? <Skeleton className="h-10" /> : (
              <>
                <span className="text-xs text-muted-foreground block">{stat.label}</span>
                <span className={`text-sm font-semibold ${stat.colorClass}`}>{stat.value}</span>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Time of Day Heatmap */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-4 h-4 text-blue-500" />
            <h3 className="text-base font-semibold">Performance by Time of Day (ET)</h3>
          </div>
          {loading ? <Skeleton className="h-20" /> : (
            <div className="flex gap-1 flex-wrap">
              {timeOfDay.map((bucket) => {
                const maxTrades = Math.max(...timeOfDay.map(b => b.trades), 1);
                const intensity = bucket.trades > 0
                  ? Math.min(15 + Math.round((bucket.trades / maxTrades) * 70), 85) : 5;
                const isPositive = bucket.win_rate >= 50;
                return (
                  <div
                    key={bucket.hour}
                    title={`${bucket.label}\nTrades: ${bucket.trades}\nWin Rate: ${bucket.win_rate}%\nP&L: ${formatDollars(bucket.total_pnl)}\nPF: ${bucket.profit_factor.toFixed(2)}`}
                    className="flex-1 min-w-[65px] p-2 rounded text-center cursor-default border"
                    style={{
                      backgroundColor: bucket.trades > 0
                        ? (isPositive ? `rgba(16,185,129,${intensity / 100})` : `rgba(244,63,94,${intensity / 100})`)
                        : 'rgba(128,128,128,0.05)',
                    }}
                  >
                    <span className="text-xs font-semibold block">
                      {bucket.hour > 12 ? `${bucket.hour - 12}PM` : bucket.hour === 12 ? '12PM' : `${bucket.hour}AM`}
                    </span>
                    {bucket.trades > 0 ? (
                      <>
                        <span className="text-sm font-bold block">{bucket.win_rate.toFixed(0)}%</span>
                        <span className="text-xs text-muted-foreground">{bucket.trades} trades</span>
                      </>
                    ) : (
                      <span className="text-xs text-muted-foreground">--</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pattern Performance Table */}
      {patterns.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <LayoutGrid className="w-4 h-4 text-blue-500" />
              <h3 className="text-base font-semibold">Pattern Performance</h3>
            </div>
            <div className="rounded-md border overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Pattern</TableHead>
                    <TableHead className="text-center">Trades</TableHead>
                    <TableHead className="text-center">Win Rate</TableHead>
                    <TableHead className="text-right">Total P&L</TableHead>
                    <TableHead className="text-center">Profit Factor</TableHead>
                    <TableHead className="text-right">Expectancy</TableHead>
                    <TableHead className="text-center">Avg Hold</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {patterns.map((p) => (
                    <TableRow key={p.pattern_id}>
                      <TableCell className="font-medium">{p.pattern_name}</TableCell>
                      <TableCell className="text-center">
                        <span className="block">{p.trades}</span>
                        <span className="text-xs text-muted-foreground">{p.wins}W/{p.losses}L</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${p.win_rate >= 50 ? 'bg-emerald-500/10 text-emerald-500' : 'bg-rose-500/10 text-rose-500'}`}>
                          {p.win_rate.toFixed(1)}%
                        </span>
                      </TableCell>
                      <TableCell className={`text-right font-semibold ${getPnlColor(p.total_pnl)}`}>{formatDollars(p.total_pnl)}</TableCell>
                      <TableCell className={`text-center font-semibold ${getProfitFactorColor(p.profit_factor)}`}>{p.profit_factor.toFixed(2)}</TableCell>
                      <TableCell className={`text-right ${getPnlColor(p.expectancy)}`}>{formatDollars(p.expectancy)}</TableCell>
                      <TableCell className="text-center text-muted-foreground">{formatMinutes(p.avg_hold_minutes)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sector Performance */}
      {sectors.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <TrendingDown className="w-4 h-4 text-blue-500" />
              <h3 className="text-base font-semibold">Sector Performance</h3>
            </div>
            <div className="rounded-md border overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Sector</TableHead>
                    <TableHead className="text-center">Trades</TableHead>
                    <TableHead className="text-center">Win Rate</TableHead>
                    <TableHead className="text-right">Total P&L</TableHead>
                    <TableHead className="text-center">Profit Factor</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sectors.map((s) => (
                    <TableRow key={s.sector}>
                      <TableCell className="font-medium">{s.sector}</TableCell>
                      <TableCell className="text-center">{s.trades}</TableCell>
                      <TableCell className={`text-center ${s.win_rate >= 50 ? 'text-emerald-500' : 'text-rose-500'}`}>{s.win_rate.toFixed(1)}%</TableCell>
                      <TableCell className={`text-right font-semibold ${getPnlColor(s.total_pnl)}`}>{formatDollars(s.total_pnl)}</TableCell>
                      <TableCell className={`text-center font-semibold ${getProfitFactorColor(s.profit_factor)}`}>{s.profit_factor.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Execution Quality */}
      {execQuality && execQuality.total_executions > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <Gauge className="w-4 h-4 text-blue-500" />
              <h3 className="text-base font-semibold">Execution Quality</h3>
              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold bg-blue-500/10 text-blue-500">
                {execQuality.smart_order_rate.toFixed(0)}% Smart Routed
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div>
                <span className="text-xs text-muted-foreground block">Total Executions</span>
                <span className="text-lg font-bold">{execQuality.total_executions}</span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block">Avg Slippage</span>
                <span className={`text-lg font-bold ${Math.abs(execQuality.avg_slippage_pct) > 0.1 ? 'text-rose-500' : 'text-emerald-500'}`}>
                  {execQuality.avg_slippage_pct.toFixed(4)}%
                </span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block">Avg Latency</span>
                <span className="text-lg font-bold">{execQuality.avg_latency_ms.toFixed(0)}ms</span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block">Success Rate</span>
                <span className={`text-lg font-bold ${execQuality.successful_executions / execQuality.total_executions >= 0.9 ? 'text-emerald-500' : 'text-amber-500'}`}>
                  {((execQuality.successful_executions / execQuality.total_executions) * 100).toFixed(1)}%
                </span>
              </div>
            </div>
            {Object.keys(execQuality.by_strategy).length > 0 && (
              <>
                <h4 className="text-sm font-semibold mb-2">By Order Strategy</h4>
                <div className="rounded-md border overflow-auto mb-4">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Strategy</TableHead>
                        <TableHead className="text-center">Orders</TableHead>
                        <TableHead className="text-right">Avg Slippage</TableHead>
                        <TableHead className="text-right">Avg Latency</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Object.entries(execQuality.by_strategy)
                        .sort(([, a], [, b]) => b.count - a.count)
                        .map(([strategy, data]) => (
                          <TableRow key={strategy}>
                            <TableCell>
                              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold bg-muted capitalize">
                                {strategy.replace(/_/g, ' ')}
                              </span>
                            </TableCell>
                            <TableCell className="text-center">{data.count}</TableCell>
                            <TableCell className={`text-right ${Math.abs(data.avg_slippage_pct) > 0.1 ? 'text-rose-500' : 'text-emerald-500'}`}>
                              {data.avg_slippage_pct.toFixed(4)}%
                            </TableCell>
                            <TableCell className="text-right">{data.avg_latency_ms.toFixed(0)}ms</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PerformanceAnalytics;
